import { InfoOutlined } from '@material-ui/icons';
import React from 'react';

import { useEscapeKey } from '../../../../utils/useEscapeKey';

interface BackNavigationPopupProps {
  /** Whether the popup is visible. */
  displayModal: boolean;
  /** Acknowledge and stay on the quote. */
  handleClose: () => void;
}

/**
 * Friendly reminder shown when the applicant presses the browser's Back button
 * on the quote page (NCP-5230). Back is absorbed by useBlockBrowserBack, so
 * without this the press would look like a broken button — the copy points them
 * at the "Edit" button on the policy card, which is the supported way back into
 * the application.
 */
const BackNavigationPopup: React.FC<BackNavigationPopupProps> = ({
  displayModal,
  handleClose
}) => {
  useEscapeKey(handleClose, displayModal);

  // Unmounted rather than hidden (unlike the sibling quote popups): this popup
  // is rendered by Quote.tsx itself, so leaving its heading in the DOM while
  // closed would put an <h2> ahead of the page's own heading hierarchy.
  if (!displayModal) {
    return null;
  }

  return (
    <div
      data-testid="back-navigation-popup"
      className="fixed w-screen h-screen top-0 left-0 bg-black bg-opacity-50 z-50"
      onClick={handleClose}
    >
      <div className="flex h-screen justify-center overflow-auto">
        <div
          className="relative max-w-xl flex flex-col h-fit-content bg-white border-2 border-white rounded-lg self-center my-8 mx-2 md:mx-0 p-6 md:p-10 text-center z-10"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex justify-center mb-4">
            <InfoOutlined style={{ fontSize: '4rem' }} />
          </div>
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            Need to change an answer?
          </h2>
          <p className="text-base md:text-lg leading-relaxed mb-8">
            Use the &quot;Edit&quot; button on the quote in order to make
            changes to your insurance application.
          </p>
          <button
            data-testid="back-navigation-popup-acknowledge"
            className="w-full bg-tertiary hover:bg-tertiary-dark border-tertiary-dark rounded-md uppercase pointer font-bold px-14 py-3 text-mobileFont md:text-xl text-white"
            onClick={handleClose}
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};

export default BackNavigationPopup;
